import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';

import {
  MetricValueClientReasonType,
  MetricValueCommentType
} from '../../../generated/graphql';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {
  StandupDashboardServiceService
} from '../../pm-stats-dashboard/standup-boards/standup-dashboard-service.service';
import { RelayIdService } from '../relay-id.service';
import { ReasonsDialogComponent } from '../reason-with-comment/reasons-dialog/reasons-dialog.component';
import { MatDialog } from '@angular/material/dialog';

export interface ReasonChipsData extends MetricValueCommentType {
  multiplier: number;
  reasonGroup: MetricValueCommentType[];
}

@Component({
  selector: 'app-reason-with-comment-v2',
  templateUrl: './reason-with-comment-v2.component.html',
  styleUrls: ['./reason-with-comment-v2.component.scss']
})
export class ReasonWithCommentV2Component implements OnInit, OnDestroy {
  subscription = new Subscription();
  needUpdate = new EventEmitter<boolean>();
  isUpdateNeeded = false;
  commentsGroup: FormGroup;
  reasonsGroup: FormGroup;
  isSaving = false;
  reasonsTitlesDict: { [key: string]: string } = { };
  metricClientReasons: MetricValueClientReasonType[] = [];
  allowedMetricGroupsToCommentReasonChange: string[] = [
    'TWV0cmljR3JvdXBUeXBlOjEw',
    'TWV0cmljR3JvdXBUeXBlOjEx',
    'TWV0cmljR3JvdXBUeXBlOjE0',
    'TWV0cmljR3JvdXBUeXBlOjEy',
    'TWV0cmljR3JvdXBUeXBlOjMw',
    'TWV0cmljR3JvdXBUeXBlOjQw'
  ];
  isReasonChangeAllowed = false;

  get comments(): FormArray { return this.commentsGroup.get('comments') as FormArray; }
  get reasons(): FormArray { return this.reasonsGroup.get('reasons') as FormArray; }

  @Input() reason: ReasonChipsData;
  @Input() managerName: string;
  @Input() metricValueCandidate: { firstName: string, lastName: string, livasId: number, employeeLivasId: number, country?: any };
  @Input() metricValueClient: { name: string, id: string, livasId: number, country?: any };
  @Input() metricGroup: string;
  @Input() metricGroupId: string;

  @Output() updateCommentsValue: EventEmitter<any> = new EventEmitter();
  @Output() updateTableData: EventEmitter<any> = new EventEmitter();

  constructor(
    private dialog: MatDialog,
    private dashboardService: StandupDashboardServiceService,
    private fb: FormBuilder,
    public relayIdService: RelayIdService,
  ) {
    this.subscription.add(this.needUpdate.subscribe(() => this.isUpdateNeeded = true));
  }

  ngOnInit(): void {
    if (this.allowedMetricGroupsToCommentReasonChange.includes(this.metricGroupId)) {
      this.dashboardService.getCommentReasonsByMetricGroup(this.metricGroupId).subscribe(res => {
        this.metricClientReasons = res as MetricValueClientReasonType[];
        this.isReasonChangeAllowed = true;
      });
    }

    this.commentsGroup = this.fb.group({
      comments: this.fb.array([])
    });

    this.reasonsGroup = this.fb.group({
      reasons: this.fb.array([])
    });

    this.reason.reasonGroup.forEach(reason => {
      this.comments.push(new FormControl(reason.comment || ''));
      this.reasons.push(new FormControl(reason.metricClientReason?.id || null));
      this.reasonsTitlesDict[reason.id] = reason.metricClientReason?.title || null;
    });
  }

  saveComment(reason: MetricValueCommentType, comment: string, reasonId: string) {
    this.isSaving = true;
    this.dashboardService.updateMetricValueComment(reason.id, comment, reasonId).subscribe(
      data => {
        const fromList = this.reason.reasonGroup.find(r => r.id === reason.id);
        if (fromList) {
          fromList.comment = comment;
          fromList.metricClientReason = { id: reasonId } as MetricValueClientReasonType;
        }
        this.reason.reasonGroup = this.reason.reasonGroup.map(v => {
          if (v.id === data.data.updateMetricValueComment.metricValueComment.id) {
            return {
              ...v,
              metricClientReason: {
                id: data.data.updateMetricValueComment.metricValueComment.metricClientReason?.id,
                title: data.data.updateMetricValueComment.metricValueComment.metricClientReason?.title
              }
            };
          }
          return v;
        }) as any;
        this.needUpdate.emit(true);
        this.isSaving = false;
      },
      () => {
        this.isSaving = false;
      }
    );
  }

  getFullComment(reason): string {
    return reason.comment;
  }

  openReasonDialog() {
    const dialog = this.dialog.open(ReasonsDialogComponent, {
      data: {
        reasonSet: this.reason.reasonGroup,
        managerName: this.managerName,
        metricGroup: this.metricGroup,
        metricGroupId: this.metricGroupId,
        metricValueCandidate: (this.metricValueCandidate.firstName || this.metricValueCandidate.lastName) ? this.metricValueCandidate : null,
        metricValueClient: this.metricValueClient.name ? this.metricValueClient : null,
        needUpdate: this.needUpdate
      },
      autoFocus: false
    });

    dialog.afterClosed().subscribe(v => {
      if (v && (Object.keys(v?.reason).length || Object.keys(v?.metricClientReason).length)) {
        this.updateTableData.emit(v);
      }
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
